import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiAlertCircle, FiLoader, FiRefreshCw } = FiIcons;

export default function VideoPlayer({ videoUrl, videoId }) {
  const [status, setStatus] = useState('loading');
  const videoRef = useRef(null);

  useEffect(() => {
    if (!videoUrl) { setStatus('missing_id'); return; }
    setStatus('loading');
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [videoUrl]);

  const handleRetry = () => {
    setStatus('loading');
    if (videoRef.current) { videoRef.current.load(); }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-black overflow-hidden">
      {videoUrl && (
        <video
          ref={videoRef}
          controls
          playsInline
          preload="metadata"
          onLoadedData={() => setStatus('ready')}
          onCanPlay={() => setStatus('ready')}
          onError={() => setStatus('error')}
          className={`w-full h-full object-contain z-10 transition-opacity duration-700 ${status === 'ready' ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src={videoUrl.replace('.webm', '.mp4')} type="video/mp4" />
          <source src={videoUrl} type="video/webm" />
        </video>
      )}

      <AnimatePresence mode="wait">
        {status === 'loading' && (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/90 text-white"
          >
            <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.5, ease: 'linear' }} className="mb-4">
              <SafeIcon icon={FiLoader} className="text-5xl text-blue-500" />
            </motion.div>
            <h3 className="text-lg font-bold tracking-wide">Syncing Secure Video</h3>
            <div className="mt-3 px-4 py-1.5 bg-white/10 rounded-full border border-white/20">
              <p className="text-xs text-slate-300 font-mono uppercase tracking-wider">
                ID: {videoId || 'Searching...'}
              </p>
            </div>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black text-white p-6 text-center"
          >
            <div className="w-14 h-14 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mb-4">
              <SafeIcon icon={FiClock} className="text-3xl text-blue-400" />
            </div>
            <h3 className="text-xl font-black mb-2">Video Processing</h3>
            <p className="text-sm text-slate-400 max-w-xs leading-relaxed mb-6">
              Your packaging video is being uploaded securely. Please check back in <strong className="text-white">2–8 hours</strong>.
            </p>
            <button
              onClick={handleRetry}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-500 active:scale-95 transition-all text-white rounded-lg text-sm font-bold shadow-lg shadow-blue-600/30"
            >
              <SafeIcon icon={FiRefreshCw} /> Refresh Status
            </button>
          </motion.div>
        )}

        {status === 'missing_id' && (
          <motion.div
            key="missing"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-black text-white p-6 text-center"
          >
            <div className="w-14 h-14 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-4">
              <SafeIcon icon={FiAlertCircle} className="text-3xl text-red-500" />
            </div>
            <h3 className="text-xl font-black mb-2">Invalid Tracking Link</h3>
            <p className="text-sm text-slate-400">Please verify your URL or contact support.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}